/** @typedef {import('headlight-configurable-application').Pict} Pict */
/** @typedef {import('./FileHandle.js')} FileHandle */

const PictProvider = require('pict-provider');

class ObservationSetProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/** @type {import('fable')} */
		this.fable;

		/** @type {any} */
		this.options;

		this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));

		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.instantiateServiceProviderIfNotExists('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });
	}

	/*
	 * Operations:
	 *
	 * Set level:
	 *
	 * Create (doesn't save)
	 * List (with optional filters)
	 * Load (by ID)
	 * Save
	 * Search
	 *
	 * Observation level:
	 *
	 * Add obs
	 * Remove obs
	 *
	 * Should not be here but here for now:
	 *
	 * Save file handle to observation
	 */

	/**
	 * @param {string} pName - The observation set name.
	 * @param {number} [pIDProject] - The project ID the set lives in.
	 *
	 * @return {ObservationSet} - The observation set object.
	 */
	createSet(pName, pIDProject = 0)
	{
		return { Name: pName || 'New Observation Set', Observations: [], IDProject: pIDProject };
	}

	/**
	 * @typedef {Object} ObservationSetFilters
	 * @property {number|string} [IDProject] - The project ID the set lives in.
	 * @property {number|string} [CreatingIDUser] - The ID of user who created the set.
	 * @property {string} [Name] - The name of the observation set.
	 * @property {Array<number>} [ObservationIDs] - Sets that container the specified observation IDs.
	 * @property {number} [Offset] - The offset to start at. Defaults to zero.
	 * @property {number} [PageSize] - The number of items to return. Defaults to 300.
	 * TODO: This is a brainstorm of what this could possibly do
	 */

	/**
	 * @typedef {Object} ObservationSet
	 * @property {number} [IDObservationSet] - The ID of the observation set.
	 * @property {number} [IDProject] - The project ID the set lives in.
	 * @property {string} [Name] - The name of the observation set.
	 * @property {string} [Description] - The description of the set.
	 * @property {number} [CreatingIDUser] - The ID of user who created the set.
	 * @property {Date} [CreateDate] - The date the set was created.
	 * @property {Array<Observation>} Observations - The observations in the set.
	 * TODO: whatever is in here
	 */

	/**
	 * @typedef {any} Observation
	 * TODO: placeholder for now
	 */

	/**
	 * Perform a JSON GET request.
	 *
	 * @param {string} uri - The URI to get.
	 * @return {Promise<any>} - The response.
	 */
	async _get(uri)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(uri, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Perform a JSON PUT request.
	 *
	 * @param {string} uri - The URI to get.
	 * @param {any} payload - The payload to send (JSON stringified).
	 * @return {Promise<any>} - The response.
	 */
	async _put(uri, payload)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.putJSON(uri, payload, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Perform a JSON POST request.
	 *
	 * @param {string} uri - The URI to get.
	 * @param {any} payload - The payload to send (JSON stringified).
	 * @return {Promise<any>} - The response.
	 */
	async _post(uri, payload)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.postJSON(uri, payload, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Perform a JSON DELETE request.
	 *
	 * @param {string} entityType - The entity type to delete.
	 * @param {number} entityID - The entity ID to delete.
	 * @return {Promise<any>} - The response.
	 */
	async _del(entityType, entityID)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.deleteEntity(entityType, entityID, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * @param {ObservationSetFilters} pFilters - The filters to apply.
	 * @return {Promise<Array<ObservationSet>>} - The list of observation sets.
	 */
	async listSets(pFilters)
	{
		let filterStanza = '';
		if (pFilters.IDProject)
		{
			if (filterStanza)
			{
				filterStanza += '~';
			}
			filterStanza += `FBV~IDProject~EQ~${pFilters.IDProject}`;
		}
		if (pFilters.CreatingIDUser)
		{
			if (filterStanza)
			{
				filterStanza += '~';
			}
			filterStanza += `FBV~CreatingIDUser~EQ~${pFilters.CreatingIDUser}`;
		}
		if (pFilters.Name)
		{
			if (filterStanza)
			{
				filterStanza += '~';
			}
			filterStanza += `FBV~Name~LK~${encodeURIComponent(`%${pFilters.Name}%`)}`;
		}
		if (pFilters.ObservationIDs)
		{
			if (filterStanza)
			{
				filterStanza += '~';
			}
			filterStanza += `FBL~ObservationIDs~INN~${pFilters.ObservationIDs.join(',')}`;
		}
		let { Offset: offset, PageSize: pageSize } = pFilters;
		if (typeof offset !== 'number' || offset < 0)
		{
			offset = 0;
		}
		if (typeof pageSize !== 'number' || pageSize < 1)
		{
			pageSize = 300;
		}
		if (filterStanza)
		{
			return await this._get(`ObservationSets/FilteredTo/${filterStanza}/${offset}/${pageSize}`);
		}
		return await this._get(`ObservationSets/${offset}/${pageSize}`);
	}

	/**
	 * @param {ObservationSet} pSet - The set to join to a Document by the current IDDocument on AppData.
	 * @return {Promise<void>}
	 */
	async joinSetToDocumentId(pSet)
	{
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		const tmpIDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument;

		if (pSet.IDObservationSet && tmpIDDocument)
		{
			const documentJoins = await this._get(`DocumentPolyJoins/By/IDDocument/${tmpIDDocument}`);
			const tmpSetJoinRecord =
			{
				RemoteType: 'ObservationSet',
				IDRemote: pSet.IDObservationSet,
				IDDocument: tmpIDDocument,
			};
			// if no joins yet, then create one
			if (documentJoins.length === 0)
			{
				await this._post(`DocumentPolyJoins`, [ tmpSetJoinRecord ]);
			}
			else
			{
				// if there is a list of joins, make sure we don't have a join already by the same ID
				const existingJoin = documentJoins.find((x) => x.IDRemote == pSet.IDObservationSet);
				if (!existingJoin)
				{
					await this._post(`DocumentPolyJoins`, [ tmpSetJoinRecord ]);
				}
			}
		}
		this.pict.providers.UploadTracking.untrackUpload(uploadID);
	}

	/**
	 * @param {ObservationSet} pSet - The set to delete the join from a Document by the current IDDocument on AppData.
	 * @return {Promise<void>}
	 */
	async deleteDocumentSetJoin(pSet)
	{
		const tmpIDDocument = this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
		if (pSet.IDObservationSet && tmpIDDocument)
		{
			const documentJoins = await this._get(`DocumentPolyJoins/By/IDDocument/${tmpIDDocument}`);
			const existingJoin = documentJoins.find((x) => x.IDRemote == pSet.IDObservationSet);
			if (existingJoin)
			{
				await this._del(`DocumentPolyJoins`, existingJoin.IDDocumentPolyJoin);
			}
		}
	}

	/**
	 * @param {ObservationSet} pIDDocument - The set to get the joins from a Document by the current IDDocument on AppData.
	 * @return {Promise<Array<any>>} - The list of "ObservationSet" joins for the current document.
	 */
	async getDocumentSetJoinList(pIDDocument)
	{
		const IDDocument = pIDDocument ?? this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
		if (IDDocument)
		{
			// filter out any joins that are not "ObservationSet" type
			return await this._get(`DocumentPolyJoins/FilteredTo/FBV~IDDocument~EQ~${IDDocument}~FBV~RemoteType~EQ~ObservationSet`);
		}
		return Promise.resolve([]);
	}

	/**
	 * @param {ObservationSet} pIDDocument - The set to get the joins from a Document by the current IDDocument on AppData.
	 * @return {Promise<Array<any>>} - The list of "ObservationSet" joins for the current document.
	 */
	async getObservationSetsForDocument(pIDDocument)
	{
		const IDDocument = pIDDocument ?? this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
		if (IDDocument)
		{
			// filter out any joins that are not "ObservationSet" type
			const tmpExistingJoins = await this.getDocumentSetJoinList(IDDocument);
			if (tmpExistingJoins && tmpExistingJoins.length > 0)
			{
				return this._get(`ObservationSets/FilteredTo/FBL~IDObservationSet~INN~${tmpExistingJoins.map((x) => x.IDRemote).join(',')}`);
			}
		}
		return Promise.resolve([]);
	}

	/**
	 * NOTE: Does not save the observation records; save them first!
	 *
	 * @param {ObservationSet} pSet - The set to save.
	 * @return {Promise<ObservationSet>} - The saved set.
	 */
	async saveSet(pSet)
	{
		// check for passing the wrong thing
		if (typeof pSet !== 'object' || !pSet)
		{
			if (typeof pSet === 'string' || typeof pSet === 'number')
			{
				this.pict.log('warn', 'saveSet is expecting a passed "set", not an ID! You passed:', pSet);
				return this.loadSet(pSet);
			}
		}
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		try
		{
			const setObservations = pSet.Observations || [];
			let setToSave = Object.assign({}, pSet);
			delete setToSave.Observations;
			if (!pSet.IDObservationSet && !this.debouncedSet)
			{
				this.debouncedSet = this._put(`ObservationSet/Upsert`, setToSave);
				setToSave = Object.assign({}, await this.debouncedSet);
			}
			else if (!pSet.IDObservationSet && this.debouncedSet)
			{
				setToSave = Object.assign(await this.debouncedSet, pSet);
			}
			else
			{
				setToSave = await this._put(`ObservationSet/Upsert`, setToSave);
				const existingObservations = await this._get(`ObservationSetObservationJoins/FilteredTo/FBV~IDObservationSet~EQ~${setToSave.IDObservationSet}`);
				const desiredObservations = setObservations.map((x) => x.IDObservation).filter((x) => x > 0);
				const toAdd = setObservations.filter((x) => !existingObservations.find((y) => y.IDObservation === x.IDObservation)).map((x) => ({ IDObservation: x.IDObservation, IDObservationSet: setToSave.IDObservationSet }));
				const toRemove = existingObservations.filter((x) => !desiredObservations.includes(x.IDObservation));

				for (const obs of toRemove)
				{
					await this._del('ObservationSetObservationJoin', obs.IDObservationSetObservationJoin);
				}
				await this._post(`ObservationSetObservationJoins`, toAdd);
			}
			Object.assign(pSet, setToSave);
		}
		finally
		{
			this.debouncedSet = null;
			// join to document if it's not already joined
			this.joinSetToDocumentId(pSet);
			this.pict.providers.UploadTracking.untrackUpload(uploadID);
			return pSet;
		}
	}

	async loadSet(pIDObservationSet)
	{
		const set = await this._get(`ObservationSet/${pIDObservationSet}`);
		const joins = await this._get(`ObservationSetObservationJoins/FilteredTo/FBV~IDObservationSet~EQ~${pIDObservationSet}`);
		set.Observations = await this._get(`Observations/FilteredTo/FBL~IDObservation~INN~${joins.map((x) => x.IDObservation).join(',')}`);
		return set;
	}

	async loadSetObservations(pIDObservationSet)
	{
		const set = await this.loadSet(pIDObservationSet);
		return set.Observations;
	}

	async isIncludedInSet(pIDObservationSet, pIDObservation)
	{
		const setObservations = await this.loadSetObservations(pIDObservationSet);
		return setObservations.map((o) => o.IDObservation).includes(parseInt(pIDObservation));
	}

	/**
	 * @param {ObservationSet} pSet - The set to add the observation to.
	 * @param {Observation} pObservation - The observation to add.
	 * @param {Number} pSetID - An ObservationSet ID to add the observation to.
	 * @return {Promise<ObservationSet>} - The set with the observation added.
	 */
	async addObservation(pSet, pObservation, pSetID)
	{
		if (!pObservation)
		{
			throw new Error('Observation is required');
		}
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		if (!pSet && pSetID)
		{
			pSet =
			{
				IDObservationSet: pSetID,
				Observations: [],
			};
		}
		if (!pSet.Observations)
		{
			pSet.Observations = [];
		}
		if (pSet.IDObservationSet && pObservation.IDObservation)
		{
			const joins = await this._get(`ObservationSetObservationJoins/FilteredTo/FBV~IDObservationSet~EQ~${pSet.IDObservationSet}~FBV~IDObservation~EQ~${pObservation.IDObservation}`);
			if (joins.length > 0) // already joined
			{
				this.pict.providers.UploadTracking.untrackUpload(uploadID);
				return pSet;
			}
			await this._post(`ObservationSetObservationJoin`, { IDObservation: pObservation.IDObservation, IDObservationSet: pSet.IDObservationSet });
		}

		pSet.Observations.push(pObservation);
		if (this.pict.providers.MediaRegistry && this.pict.providers.DocumentProvider)
		{
			this.pict.providers.MediaRegistry.updateNamespacesByIDObservationSet(pSet.IDObservationSet, pSet.Observations);
		}
		this.pict.providers.UploadTracking.untrackUpload(uploadID);
		return pSet;
	}

	/**
	 * @param {ObservationSet} pSet - The set to remove the observation from.
	 * @param {Observation} pObservation - The observation to remove.
	 * @return {Promise<ObservationSet>} - The set with the observation removed.
	 */
	async removeObservation(pSet, pObservation)
	{
		if (!pObservation)
		{
			throw new Error('Observation is required');
		}
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		if (!pSet.Observations)
		{
			pSet.Observations = [];
		}

		const index = pSet.Observations.findIndex((x) => x === pObservation);
		if (index >= 0)
		{
			pSet.Observations.splice(index, 1);
		}
		if (pSet.IDObservationSet && pObservation.IDObservation)
		{
			const joins = await this._get(`ObservationSetObservationJoins/FilteredTo/FBV~IDObservationSet~EQ~${pSet.IDObservationSet}~FBV~IDObservation~EQ~${pObservation.IDObservation}`);
			if (joins.length > 0)
			{
				await this._del(`ObservationSetObservationJoin`, joins[0].IDObservationSetObservationJoin);
			}
		}
		if (this.pict.providers.MediaRegistry && this.pict.providers.DocumentProvider)
		{
			this.pict.providers.MediaRegistry.updateNamespacesByIDObservationSet(pSet.IDObservationSet, pSet.Observations);
		}
		this.pict.providers.UploadTracking.untrackUpload(uploadID);
		return pSet;
	}

	/**
	 * @param {Observation} pObservation - The observation to save.
	 * @param {FileHandle} pFileHandle - The file handle to save
	 * @return {Promise<Observation>} - The saved observation.
	 */
	async createObservationFromFileHandle(pObservation, pFileHandle)
	{
		throw new Error('Not implemented');
		//FIXME: implement
		// 1. create obs
		// 2. upload file
		// 3. create tracking relationship / metadata (needs definition)
	}
}

module.exports = ObservationSetProvider;
module.exports.default_configuration = {};
